<script setup lang="ts">
import { computed } from 'vue'
import { givingRecordRowToDonorDonation } from '@/helpers/givingRecordDonation'
import DonationListRow from '@/components/dashboard/ui/DonationListRow.vue'
import DonationListRowSkeleton from '@/components/dashboard/giving/DonationListRowSkeleton.vue'
import type { GivingRecordRow } from '@/types/givingRecord'

const props = withDefaults(
  defineProps<{
    rows: GivingRecordRow[]
    variant: 'all' | 'swiftaid' | 'manual'
    loading?: boolean
  }>(),
  {
    loading: false,
  }
)

const regionLabelKey = computed(() => {
  if (props.variant === 'swiftaid') return 'Dashboard.Giving.Table.CaptionSwiftaid'
  if (props.variant === 'manual') return 'Dashboard.Giving.Table.CaptionManual'
  return 'Dashboard.Giving.Table.CaptionAll'
})

const showEmptyState = computed(
  () => !props.loading && props.rows.length === 0
)

const showSkeletons = computed(() => props.loading)

const listStatusLabel = computed(() => {
  if (props.loading && props.rows.length === 0) {
    return 'Dashboard.Giving.Loading'
  }
  if (props.loading) {
    return 'Dashboard.Giving.Updating'
  }
  return undefined
})
</script>

<template>
  <div class="sa-donation-list" role="region" :aria-label="$t(regionLabelKey)">
    <p
      v-if="listStatusLabel"
      class="sa-donation-list__status sa-sr-only"
      role="status"
      aria-live="polite"
    >
      {{ $t(listStatusLabel) }}
    </p>

    <template v-if="showSkeletons">
      <DonationListRowSkeleton v-for="index in 5" :key="`skeleton-${index}`" />
    </template>

    <p v-else-if="showEmptyState" class="sa-donation-list__empty-state">
      {{ $t('Dashboard.Giving.Table.Empty') }}
    </p>

    <template v-else>
      <DonationListRow
        v-for="row in rows"
        :key="row.id"
        :donation="givingRecordRowToDonorDonation(row)"
        :show-source="variant === 'all'"
      />
    </template>
  </div>
</template>
